import React from 'react';
import _ from 'underscore';

import {Genome} from 'constants/GenomeConstants';
import GenomeImportHelpText from 'molbio/genome/GenomeImportHelpText';
import LegacySelect2, {LegacySelect2ChangeEvent} from 'ui-library/LegacySelect2';
import escapeRegExp from 'util/escapeRegExp';
import {genomeToName} from 'util/GenomeUtil';

interface GenomeSelectorProps {
  disabled?: boolean;
  genomes: Array<Genome>;
  genomeLink: {
    value: string;
    requestChange: (e: string) => void;
  };
}

function GenomeSelector({disabled = false, genomes, genomeLink}: GenomeSelectorProps): JSX.Element {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const genomeChangeHandler = (e: LegacySelect2ChangeEvent) => {
    genomeLink.requestChange(e.val);
  };
  const optionData = genomes.map((genome) =>
    // eslint-disable-next-line no-restricted-syntax
    _.extend({}, genome, {
      id: genome.ensemblName,
      text: genomeToName(genome),
      dataTestAttrs: {component: 'GenomeSelector', element: 'option'},
    })
  );
  const genomeOptions = _.chain(optionData)
    // TODO(syu): Sorting by ucscName is broken for version numbers since it's doing a
    // lexicographical sort (e.g., mm10 is before mm9)
    .sortBy((genomeOption) => genomeOption.ucscName)
    .reverse()
    .sortBy((genomeOption) => genomeOption.organismName)
    .value();

  return (
    <div>
      <LegacySelect2
        placeholder="Genome"
        options={genomeOptions}
        value={genomeLink.value}
        onChange={genomeChangeHandler}
        matcher={function (params: string, text: string, data: Genome): boolean {
          // params is the user's search query. Split it and search for substrings across all genome
          // names. For example, 'd m' matches 'Drosophila melanogaster'

          const escaped = escapeRegExp(params);
          const terms = _.compact(escaped.split(/\s+/));

          // Return true if each term matches something in the genome
          return terms.every((term) => {
            const regex = new RegExp(term, 'i');
            return (
              regex.test(data.ucscName || '') ||
              regex.test(data.organismName) ||
              regex.test(data.ensemblName) ||
              regex.test(data.organism != null ? data.organism.keywords : '')
            );
          });
        }}
        disabled={disabled}
        dataTestAttrs={{component: 'GenomeSelector', element: 'selectRoot'}}
      />
      <GenomeImportHelpText />
    </div>
  );
}

export default GenomeSelector;
